import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { OmniMindChat } from './components/chat/OmniMindChat';
import { LoginForm } from './components/auth/LoginForm';
import { DatasourcesPage } from './pages/settings/DatasourcesPage';
import { DailyReportPage } from './pages/DailyReportPage';
import { ReportHistoryPage } from './pages/ReportHistoryPage';
import { ExecutiveDashboardPage } from './pages/analytics/ExecutiveDashboardPage';
import { DataSourceHealthPage } from './pages/analytics/DataSourceHealthPage';
import { SearchAnalyticsPage } from './pages/analytics/SearchAnalyticsPage';
import { TrendAnalysisPage } from './pages/analytics/TrendAnalysisPage';
import { AnomalyDetectionPage } from './pages/analytics/AnomalyDetectionPage';
import { DocumentIntelligencePage } from './pages/analytics/DocumentIntelligencePage';
import { UserAdoptionPage } from './pages/analytics/UserAdoptionPage';
import { ReportAnalyticsPage } from './pages/analytics/ReportAnalyticsPage';
import { DecisionRoomPage } from './pages/decide/DecisionRoomPage';
// v2 core surfaces. These read the real API; the pages they sit beside are
// static mockups from src/demo and are retired as each real screen lands.
import { MetricRegisterPage } from './core/metrics/MetricRegisterPage';
import { DecisionInboxPage } from './core/inbox/DecisionInboxPage';
import { ProcessIntakePage } from './core/process/ProcessIntakePage';
import { ConsultantPage } from './core/consultant/ConsultantPage';
import {
  SemanticLayerPage,
  AgentsPage,
  TrustPage,
  DecisionLogPage,
} from './pages/decide/GovernancePages';
import {
  UsersPage,
  DepartmentsPage,
  CategoryPermissionsPage,
  AccessRequestsPage,
  SsoPage,
} from './pages/settings/AdminPages';

/** Screens available to any signed-in user */
const SCREENS: { path: string; element: React.ReactNode }[] = [
  { path: '/', element: <OmniMindChat /> },
  { path: '/daily-report', element: <DailyReportPage /> },
  { path: '/daily-reports', element: <ReportHistoryPage /> },
  { path: '/reports', element: <ReportHistoryPage /> },
  { path: '/analytics/executive', element: <ExecutiveDashboardPage /> },
  { path: '/analytics/datasource-health', element: <DataSourceHealthPage /> },
  { path: '/analytics/health', element: <DataSourceHealthPage /> },
  { path: '/analytics/search', element: <SearchAnalyticsPage /> },
  { path: '/analytics/trends', element: <TrendAnalysisPage /> },
  { path: '/analytics/anomalies', element: <AnomalyDetectionPage /> },
  { path: '/analytics/documents', element: <DocumentIntelligencePage /> },
  { path: '/analytics/adoption', element: <UserAdoptionPage /> },
  { path: '/analytics/reports', element: <ReportAnalyticsPage /> },
  // The real inbox, reading /api/core/cards.
  { path: '/consultant', element: <ConsultantPage /> },
  { path: '/process', element: <ProcessIntakePage /> },
  { path: '/decisions', element: <DecisionInboxPage /> },
  // The original Decision Room mockup, kept as the design target.
  { path: '/decisions/mockup', element: <DecisionRoomPage /> },
  // The real register, reading /api/core/metrics.
  { path: '/metrics', element: <MetricRegisterPage /> },
  // The original mockup, kept at its own path until the real screen covers
  // everything it showed. It is the design target, not a fallback.
  { path: '/metrics/mockup', element: <SemanticLayerPage /> },
  { path: '/agents', element: <AgentsPage /> },
  { path: '/trust', element: <TrustPage /> },
  { path: '/log', element: <DecisionLogPage /> },
  { path: '/settings/users', element: <UsersPage /> },
  { path: '/settings/departments', element: <DepartmentsPage /> },
  { path: '/settings/category-permissions', element: <CategoryPermissionsPage /> },
  { path: '/settings/access-requests', element: <AccessRequestsPage /> },
  { path: '/settings/sso', element: <SsoPage /> },
];

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginForm />} />

          {SCREENS.map(({ path, element }) => (
            <Route key={path} path={path} element={<ProtectedRoute>{element}</ProtectedRoute>} />
          ))}

          <Route path="/analytics" element={<Navigate to="/analytics/executive" replace />} />

          <Route
            path="/settings/datasources"
            element={
              <RoleProtectedRoute allowedRoles={['ceo', 'cfo', 'org_admin', 'super_admin']}>
                <DatasourcesPage />
              </RoleProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

// Shared splash used while the session is being resolved
function Splash({ label }: { label: string }) {
  return (
    <div className="app" style={{ display: 'grid', placeItems: 'center' }}>
      <div className="animate-fade-in" style={{ textAlign: 'center' }}>
        <div className="orb animate-glow">
          <i />
        </div>
        <p className="dim" style={{ fontWeight: 700, fontSize: 12.5 }}>
          {label}
        </p>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <Splash label="Connecting to your sources…" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Role-based Protected Route Component
function RoleProtectedRoute({
  children,
  allowedRoles
}: {
  children: React.ReactNode;
  allowedRoles: string[];
}) {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return <Splash label="Checking your permissions…" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasRole(allowedRoles)) {
    return (
      <div className="app" style={{ display: 'grid', placeItems: 'center' }}>
        <div
          className="card animate-scale-in"
          style={{ maxWidth: 420, padding: '26px 26px 22px', textAlign: 'center' }}
        >
          <div
            className="kic"
            style={{
              background: 'var(--red-s)',
              color: 'var(--red)',
              margin: '0 auto 12px',
              width: 40,
              height: 40,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.9}
              strokeLinecap="round"
            >
              <rect x="5" y="10" width="14" height="10" rx="2.5" />
              <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" />
            </svg>
          </div>
          <h3 style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.035em' }}>Access denied</h3>
          <p className="dim" style={{ fontSize: 12.5, margin: '7px 0 18px', lineHeight: 1.55 }}>
            Your role does not include this page. Permissions are inherited from your source
            systems — ask your administrator if you need it.
          </p>
          <button onClick={() => window.history.back()} className="btn btn-p">
            Go back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default App;
